import { StylesConfig } from 'react-select'

export type MyOptionType = {
  label: JSX.Element | string
  value: string
}

export type SimpleOptionType = {
  label: string
  value: string
  parentId?: string
}

type IsMulti = boolean

export const customSelectStyles: StylesConfig<SimpleOptionType, false> = {
  control: (provided) => ({
    ...provided,
    borderRadius: '8px',
    border: '1px solid #d3daee', // seabiscuit gray 500 theme color
    boxShadow: 'none',
    height: '42px', //42 px to match the Increment components 40px height and 2px parent padding
    '&:hover': { borderColor: '#777E85' }, // seabiscuit gray 600 theme color
    fontSize: '14px',
  }),
  dropdownIndicator: (provided) => ({
    ...provided,
    color: '#4B5563',
    '& svg': {
      fill: '#4B5563',
    },
  }),
  indicatorSeparator: () => ({
    display: 'none',
  }),
  menu: (provided) => ({
    ...provided,
    borderRadius: '8px', //  border radius on dropdown
  }),
  menuList: (provided) => ({
    ...provided,
    padding: '8px',
    '&::-webkit-scrollbar': {
      width: '4px',
    },
    '&::-webkit-scrollbar-track': {
      background: 'transparent',
      borderRadius: '8px',
      margin: '2px 0', // small margin to top and bottom makes scrollbar look better
    },
    '&::-webkit-scrollbar-thumb': {
      background: '#d1d5db',
      borderRadius: '10px',
    },
  }),
  option: (provided, state) => ({
    ...provided,
    borderRadius: '8px',
    backgroundColor: state.isSelected ? '#fff' : state.isFocused ? '#F6F7FB' : '#fff',
    color: '#122b46',
    '&:hover': {
      backgroundColor: '#f6f7fb',
    },
  }),
}

export const customSelectActiveStyles = (value: string): StylesConfig<SimpleOptionType, false> => {
  return {
    control: (provided) => ({
      ...provided,
      ...customSelectStyles.control,
      backgroundColor: value ? '#F707630D' : 'transparent',
      borderColor: value ? '#F707630D' : '#d3daee',
    }),
    singleValue: (provided) => ({
      ...provided,
      ...customSelectStyles.control,
      color: value ? '#F70763' : '#122b46',
    }),
    dropdownIndicator: (provided) => ({
      ...provided,
      ...customSelectStyles.dropdownIndicator,
      color: value ? '#F70763' : '#4B5563',
      '& svg': {
        fill: value ? '#F70763' : '#4B5563',
      },
    }),
  }
}

// increment-specific styles for the time selector components
const baseIncrementSelectStyles: StylesConfig<SimpleOptionType, IsMulti> = {
  control: (provided) => ({
    ...provided,
    border: 'none',
    boxShadow: 'none',
    padding: '0px !important',
    minHeight: '40px',
    display: 'flex',
    alignItems: 'center',
  }),
  singleValue: (provided) => ({
    ...provided,
    fontSize: '14px',
    color: '#122b46',
    textAlign: 'center',
  }),
  input: (styles) => ({
    ...styles,
    width: '30px',
    color: '#122b46',
  }),
  menuList: (provided) => ({
    ...provided,
    backgroundColor: '#fff',
    fontSize: '14px',
    padding: '4px',
    boxShadow: 'none',
    borderRadius: '8px',
    '&::-webkit-scrollbar-track': {
      borderRadius: '8px',
      margin: '1px 0',
    },
  }),
  menu: (provided) => ({
    ...provided,
    backgroundColor: '#fff',
    zIndex: '9999',
    borderRadius: '8px',
    minWidth: '50px',
    width: 'auto',
  }),
  option: (provided, state) => ({
    ...provided,
    borderRadius: '8px',
    backgroundColor: state.isSelected ? '#fff' : state.isFocused ? '#F6F7FB' : '#fff',
    color: '#122b46',
    '&:hover': {
      backgroundColor: '#f6f7fb',
    },
  }),
  dropdownIndicator: () => ({
    display: 'none',
  }),
}

// White background variant
export const incrementSelectStyles: StylesConfig<SimpleOptionType, IsMulti> = {
  ...baseIncrementSelectStyles,
  control: (provided, props) => ({
    ...baseIncrementSelectStyles.control!(provided, props),
    backgroundColor: '#fff',
  }),
  valueContainer: (provided) => ({
    ...provided,
    padding: '0px',
  }),
  singleValue: (provided) => ({
    ...provided,
    margin: '0px',
    fontSize: '14px',
    textAlign: 'center',
  }),
  input: (provided) => ({
    ...provided,
    cursor: 'pointer',
    width: '20px',
  }),
}

// Transparent background variant
export const incrementSelectStylesTransparent: StylesConfig<SimpleOptionType, IsMulti> = {
  ...baseIncrementSelectStyles,
  control: (provided, props) => ({
    ...baseIncrementSelectStyles.control!(provided, props),
    backgroundColor: 'transparent',
  }),
}

export const gapSelectStyles: StylesConfig<SimpleOptionType, IsMulti> = {
  control: (provided) => ({
    ...provided,
    boxShadow: 'none',
    padding: '0px !important',
    margin: '0px',
    minHeight: '40px',
    width: '23px',
    border: 'none',
    color: 'red !important',
    '&:focus': {
      outline: 'none',
      boxShadow: 'none',
    },
    '&:focus-within': {
      outline: 'none',
      boxShadow: 'none',
    },
  }),
  singleValue: (provided) => ({
    ...provided,
    fontSize: '14px',
    color: '#122B46',
    padding: '0px',
    margin: '0px',
    height: '100%',
    textAlign: 'center',
  }),
  placeholder: (provided) => ({
    ...provided,
    fontSize: '14px',
    color: '#122B46',
    opacity: 0.5,
  }),
  input: (styles) => ({
    ...styles,
    color: '#122B46',
    fontSize: '14px !important',
    opacity: 0.5,
    margin: '0px !important',
    padding: '0px !important',
  }),
  valueContainer: (provided) => ({
    ...provided,
    padding: '0px',
    margin: '0px',
    textAlign: 'center',
    justifyContent: 'center',
  }),
  menuList: (provided) => ({
    ...provided,
    backgroundColor: '#fff',
    fontSize: '14px',
    padding: '4px',
    boxShadow: 'none',
    borderRadius: '8px',
    '&::-webkit-scrollbar-track': {
      borderRadius: '8px',
      margin: '2px 0', // small margin to top and bottom makes scrollbar look better
    },
  }),
  menu: (provided) => ({
    ...provided,
    backgroundColor: '#fff',
    zIndex: '9999',
    borderRadius: '8px',
    minWidth: '50px',
    width: 'auto',
  }),
  option: (provided, state) => ({
    ...provided,
    borderRadius: '8px',
    backgroundColor: state.isSelected ? '#fff' : state.isFocused ? '#F6F7FB' : '#fff',
    color: '#122b46',
    '&:hover': {
      backgroundColor: '#f6f7fb',
    },
  }),
  dropdownIndicator: () => ({
    display: 'none',
  }),
}

// Custom styling for RideTimeSelector
export const rideTimeSelectStyles = {
  control: (provided: any) => ({
    ...provided,
    backgroundColor: '#f6f7fb',
    border: 'none',
    boxShadow: 'none',
    padding: '0px !important',
    minHeight: '40px',
    display: 'flex',
    alignItems: 'center',
  }),
  singleValue: (provided: any) => ({
    ...provided,
    fontSize: '14px',
    color: '#122b46',
  }),
  input: (styles: any) => ({
    ...styles,
    width: '30px',
    cursor: 'pointer',
    color: '#122b46',
  }),
  menuList: (provided: any) => ({
    ...provided,
    backgroundColor: '#fff',
    fontSize: '14px',
    padding: '4px',
    boxShadow: 'none',
    borderRadius: '8px',
    '&::-webkit-scrollbar-track': {
      borderRadius: '8px',
      margin: '1px 0', // small margin to top and bottom makes scrollbar look better
    },
  }),
  menu: (provided: any) => ({
    ...provided,
    backgroundColor: '#fff',
    zIndex: '9999',
    borderRadius: '8px',
    minWidth: '50px',
    width: 'auto',
  }),
  option: (provided: any, state: any) => ({
    ...provided,
    borderRadius: '8px',
    backgroundColor: state.isSelected ? '#fff' : state.isFocused ? '#F6F7FB' : '#fff',
    color: '#122b46',
    '&:hover': {
      backgroundColor: '#f6f7fb',
    },
  }),
  dropdownIndicator: () => ({
    display: 'none',
  }),
}
